import React from 'react';

import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import { Box, IconButton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { formatCountry } from '~/components/GHAParticipationReport/utils';

import type { CompetitorHotelOpportunities } from '~/types/services/priceParrot';

import { formatNumber } from '~/utils/numberFormatters';

import { OPPORTUNITIES_DETAIL_COUNTRIES, SELECTED_USER_LOCATION_SX } from './constants';
import { formatDateRange, formatShare, userLocationsFirst } from './utils';

interface Props {
  hotelName: string;
  opportunities: CompetitorHotelOpportunities;
  userLocations: Array<string>;
  pinned: boolean;
  onPinnedChange: (pinned: boolean) => void;
}

export default function CompetitorOfferOpportunitiesDetail({
  hotelName,
  opportunities,
  userLocations,
  pinned,
  onPinnedChange,
}: Props) {
  const { countryCount, opportunities: total, firstReportDate, lastReportDate } = opportunities;
  const isSelected = (countryCode: string | undefined) => !!countryCode && userLocations.includes(countryCode);
  const ordered = userLocationsFirst(opportunities.topCountries, userLocations);
  const selectedCount = ordered.filter((country) => isSelected(country.countryCode)).length;
  const topCountries = ordered.slice(0, selectedCount + OPPORTUNITIES_DETAIL_COUNTRIES);
  const otherCountries =
    countryCount - topCountries.filter((country) => opportunities.topCountries.includes(country)).length;
  const otherOpportunities = total - topCountries.reduce((sum, country) => sum + country.opportunities, 0);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} pl={2}>
        <Box>
          <Typography variant="subtitle2">{hotelName}</Typography>
          <Typography variant="caption" color="text.secondary">
            Google Hotel Ads
            {firstReportDate && lastReportDate && ` · ${formatDateRange(firstReportDate, lastReportDate)}`}
          </Typography>
        </Box>
        <IconButton
          size="small"
          aria-label={pinned ? 'Unpin' : 'Pin'}
          color={pinned ? 'primary' : 'default'}
          onClick={() => onPinnedChange(!pinned)}
        >
          {pinned ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
        </IconButton>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { bgcolor: 'transparent', color: 'text.secondary', fontWeight: 'normal', py: 0.5 } }}>
            <TableCell>User location</TableCell>
            <TableCell align="right">Opportunities</TableCell>
            <TableCell align="right">Share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {topCountries.map((country) => (
            <TableRow
              key={country.countryCode ?? 'unknown'}
              sx={
                isSelected(country.countryCode)
                  ? { '& td': { fontWeight: 'bold', ...SELECTED_USER_LOCATION_SX } }
                  : undefined
              }
            >
              <TableCell>{country.countryCode ? formatCountry(country.countryCode) : 'Unknown'}</TableCell>
              <TableCell align="right">{formatNumber(country.opportunities)}</TableCell>
              <TableCell align="right">{formatShare(country.opportunities, total)}</TableCell>
            </TableRow>
          ))}
          {otherCountries > 0 && (
            <TableRow>
              <TableCell>Other ({otherCountries})</TableCell>
              <TableCell align="right">{formatNumber(otherOpportunities)}</TableCell>
              <TableCell align="right">{formatShare(otherOpportunities, total)}</TableCell>
            </TableRow>
          )}
          <TableRow>
            <TableCell sx={{ fontWeight: 'medium', borderBottom: 0 }}>Total</TableCell>
            <TableCell align="right" sx={{ fontWeight: 'medium', borderBottom: 0 }}>
              {formatNumber(total)}
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'medium', borderBottom: 0 }}>
              100%
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
}
